import React, { useEffect } from 'react';
import { Box, FormControlLabel, FormControlLabelProps, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { createSelector } from 'reselect';

import { getModifierKeys } from '@paragon/utilities';

import { RootState } from '../../../store';
import { getBasicInsertionInformation } from '../../../store/selectors/basicInsertionSelectors';
import { getBearingInsertionInfo } from '../../../store/selectors/bearingInsertionSelectors';
import { getActiveEditType } from '../../../store/selectors/interactiveEditingSelectors';
import { getMousyProfileInsertionInformation } from '../../../store/selectors/mousyProfileInsertionSelectors';
import {
  getPickingObjectInfoType,
  getPointPickingFixedDistance,
  getPointPickingOtherPoint,
} from '../../../store/selectors/pickingSelectors';
import { getActualPointSnappingSettings } from '../../../store/selectors/snappingSettingsSelectors';
import { getTrussEnvelopeInsertionInformation } from '../../../store/selectors/trussEnvelopeInsertionInformationSelectors';
import { getWorkLineInsertionInfo } from '../../../store/selectors/workLineInsertionSelectors';
import { setSnappingLevel, SnappingLevel } from '../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { StandardFloatingBox } from './common/StandardFloatingBox';

function FormControlLabelWithKeyboardShortcut(
  props: Omit<FormControlLabelProps, 'label'> & { labelText: string; shortcutText: string },
) {
  const { labelText, shortcutText, ...formControlProps } = props;

  return (
    <FormControlLabel
      {...formControlProps}
      sx={[
        {
          marginRight: 0,
          '&>:nth-child(2)': {
            flex: 1,
            display: 'inline-flex',
          },
        },

        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
      label={
        <>
          {labelText}
          <span style={{ marginRight: 'auto' }} />
          <Box component="span" sx={{ color: 'text.secondary', marginLeft: 1 }}>
            {shortcutText}
          </Box>
        </>
      }
    />
  );
}

export const getDistanceSnappingLevelStatus = createSelector(
  [
    getBasicInsertionInformation,
    getWorkLineInsertionInfo,
    getBearingInsertionInfo,
    getMousyProfileInsertionInformation,
    getTrussEnvelopeInsertionInformation,
    getActiveEditType,
    getPickingObjectInfoType,
    getPointPickingOtherPoint,
    getPointPickingFixedDistance,
    getActualPointSnappingSettings,
    (state: RootState) => state.application.snappingLevel,
  ],
  (
    basicInsertionInfo,
    workLineInsertionInfo,
    bearingInsertionInfo,
    mousyProfileInsertionInfo,
    trussEnvelopeInsertionInformation,
    activeEditType,
    pickingObjectInfoType,
    pointPickingOtherPoint,
    pointPickingFixedDistance,
    actualPointSnappingSettings,
    level,
  ): { active: false } | { active: true; level: SnappingLevel } => {
    if (basicInsertionInfo.inserting && basicInsertionInfo.definition.firstSelection?.type === 'line') {
      return { active: true, level };
    }

    if (
      workLineInsertionInfo?.draft.phase === 'gesturing' &&
      workLineInsertionInfo.draft.firstSelection.type === 'line'
    ) {
      return { active: true, level };
    }

    if (bearingInsertionInfo) {
      return { active: true, level };
    }

    if (mousyProfileInsertionInfo) {
      return { active: true, level };
    }

    if (
      trussEnvelopeInsertionInformation?.settings.snapping.lockedTarget &&
      trussEnvelopeInsertionInformation.settings.snapping.distanceSettings === 'automatic'
    ) {
      return { active: true, level };
    }

    if (activeEditType === 'bearingEnvelopeLength') {
      return { active: true, level };
    }

    if (
      pickingObjectInfoType === 'pickingLayoutPoint' &&
      pointPickingOtherPoint != null &&
      pointPickingFixedDistance == null &&
      actualPointSnappingSettings.segmentSnaps.lengthIncrement
    ) {
      return { active: true, level };
    }

    return { active: false };
  },
);

export function SnappingLevelFloatingBoxAndEventListeners() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(getDistanceSnappingLevelStatus);

  const { active } = status;
  const snappingLevel = active ? status.level : null;

  useEffect(() => {
    if (!snappingLevel) return;

    function onKeyDown(event: KeyboardEvent) {
      if (!snappingLevel) return;

      if (event.key === 'Control' && snappingLevel !== 'fine') {
        dispatch(setSnappingLevel('fine'));
      } else if (event.key === 'Shift' && snappingLevel !== 'coarse') {
        dispatch(setSnappingLevel('coarse'));
      }
    }

    function onKeyUp(event: KeyboardEvent) {
      if (!snappingLevel) return;

      if (
        (snappingLevel === 'fine' && !getModifierKeys(event).ctrlKey) ||
        (snappingLevel === 'coarse' && !event.shiftKey)
      ) {
        dispatch(setSnappingLevel('default'));
      }
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [dispatch, snappingLevel]);

  useEffect(() => {
    if (!active) return;

    return () => {
      dispatch(setSnappingLevel('default'));
    };
  }, [dispatch, active]);

  if (snappingLevel) {
    return (
      <StandardFloatingBox>
        <Stack direction="column" spacing={1}>
          <Typography variant="h5" gutterBottom>
            Distance
          </Typography>

          <RadioGroup
            value={snappingLevel}
            onChange={(event) => {
              dispatch(setSnappingLevel(event.target.value as SnappingLevel));
            }}
          >
            <FormControlLabelWithKeyboardShortcut
              value="fine"
              control={<Radio size="small" />}
              labelText="&frac14; inches"
              shortcutText="hold ctrl"
            />

            <FormControlLabelWithKeyboardShortcut
              value="default"
              control={<Radio size="small" />}
              labelText="inches"
              shortcutText="default"
            />

            <FormControlLabelWithKeyboardShortcut
              value="coarse"
              control={<Radio size="small" />}
              labelText="feet"
              shortcutText="hold shift"
            />
          </RadioGroup>
        </Stack>
      </StandardFloatingBox>
    );
  }

  return null;
}
